import BaseComponent from 'scripts/BaseComponent.js';
import Swiper, { FreeMode } from 'swiper';

export default class CollectionHatboxNav extends BaseComponent {
  constructor(component) {
    super(component);
    this.debug = false;

    this.refs = {
      container: '.js-hatboxNavContainer',
      navigation: '.js-navigation',
      navObserver: '.js-hatboxNavObserver',
      navSpacer: '.js-hatboxNavSpacer',
      navBtn: '.js-hatboxNavBtn',
      shapeContainer: '.js-hatboxShapeContainer',
    };

    this.dom = {
      $component: component,
      $container: this.$get(this.refs.container),
      $navigation: document.querySelector(this.refs.navigation),
      $navObserver: document.querySelector(this.refs.navObserver),
      $navSpacer: document.querySelector(this.refs.navSpacer),
      $navBtns: this.$getAll(this.refs.navBtn),
    };

    this.state = {
      breakpoint: '(min-width: 1024px)',
      swiper: null,
      fixedNavClass: 'is-fixed',
      activeBtnClass: 'is-active',
      activeShapeSection: this.dom.$navBtns[0].dataset.shape,
      updateHatboxNavEvent: 'updatehatboxnav',
      isAnimatingPageScroll: false,
    };

    this.mount();
  }

  mount() {
    const { dom, state } = this;
    super.mount();

    this.observe();
    this.checkNavSlider();
    window.addEventListener('resize', this.checkNavSlider.bind(this));
    this.setClickWithElement(dom.$navBtns, this.navBtnClick);
    this.setOnCustomEvent(state.updateHatboxNavEvent, this.pageScrollHandler);
  }

  isDesktop() {
    const { state: { breakpoint } } = this;
    return window.matchMedia(breakpoint).matches;
  }

  observe() {
    const { dom } = this;
    const offset = dom.$navigation.offsetHeight;

    const observer = new IntersectionObserver(
      this.onIntersection.bind(this),
      { rootMargin: `-${offset}px 0px 0px 0px` },
    );
    observer.observe(dom.$navObserver);
  }

  onIntersection([entry]) {
    const { dom, state } = this;
    if (this.isDesktop()) return;

    if (entry.isIntersecting) {
      dom.$component.classList.remove(state.fixedNavClass);
      dom.$navSpacer.classList.remove(state.fixedNavClass);
    } else {
      dom.$component.classList.add(state.fixedNavClass);
      dom.$navSpacer.classList.add(state.fixedNavClass);
    }
  }

  checkNavSlider() {
    if (!this.isDesktop()) {
      this.initNavSlider();
    } else {
      this.destroyNavSlider();
    }
  }

  initNavSlider() {
    const { dom: { $container }, state } = this;
    if (state.swiper) return;

    state.swiper = new Swiper($container, {
      modules: [FreeMode],
      direction: 'horizontal',
      slidesPerView: 'auto',
      freeMode: {
        enabled: true,
      },
    });
  }

  destroyNavSlider() {
    const { state } = this;
    if (!state.swiper) return;

    state.swiper.destroy();
    state.swiper = null;
  }

  async navBtnClick(btn) {
    const { dom, state, refs } = this;
    const { shape } = btn.dataset;

    this.setActiveBtn(shape);

    const selector = `${refs.shapeContainer}[data-shape="${shape}"]`;
    const section = document.querySelector(selector);
    let offset = section.offsetTop - dom.$navigation.offsetHeight;

    if (!this.isDesktop()) {
      offset -= 65;
    }

    state.isAnimatingPageScroll = true;
    await this.animateScroll(offset);
    state.isAnimatingPageScroll = false;
  }

  setActiveBtn(shape) {
    const { state, refs } = this;
    if (state.activeShapeSection === shape) return;

    const currentActiveBtnSelector = `${refs.navBtn}[data-shape="${state.activeShapeSection}"]`;
    this.$get(currentActiveBtnSelector).classList.remove(state.activeBtnClass);

    const activeBtnSelector = `${refs.navBtn}[data-shape="${shape}"]`;
    this.$get(activeBtnSelector).classList.add(state.activeBtnClass);

    state.activeShapeSection = shape;
  }

  pageScrollHandler({ detail: { shape, index } }) {
    const { state: { isAnimatingPageScroll } } = this;
    if (isAnimatingPageScroll) return;

    this.setActiveBtn(shape);

    const { state: { swiper } } = this;
    if (!swiper) return;

    swiper.slideTo(index);
  }

  animateScroll(offset) {
    return new Promise((resolve) => {
      this.animateScrollRun(offset, resolve);
    });
  }

  animateScrollRun(offset, callback) {
    if (this.isAnimatedScrollComplete(offset)) {
      callback();
      return;
    }

    const onScroll = () => {
      if (!this.isAnimatedScrollComplete(offset)) return;
      window.removeEventListener('scroll', onScroll);
      callback();
    };

    window.addEventListener('scroll', onScroll);
    window.scrollTo({
      top: offset,
      left: 0,
      behavior: 'smooth',
    });
  }

  isAnimatedScrollComplete(offset) {
    const fixedOffset = offset.toFixed();
    const scrolledOffset = window.scrollY.toFixed();

    return scrolledOffset === fixedOffset;
  }
}
